import React, { useMemo, useState, useEffect, useContext, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, ArrowLeft } from 'lucide-react';
import { useViewConfig } from '../hooks/useViewConfig';
import { apiFetch } from '../utils/api';
import { removeFromCart, addToCart, updateSectionNote, clearCart, setCartItems, updateCartItemQty, setAppliedCoupon, removeAppliedCoupon } from '../redux/cartSlice';
import { SECTION_RULES } from '../utils/cartRules';
import { FREE_DELIVERY_THRESHOLD, DELIVERY_CHARGE, PACKING_CHARGE_STANDARD, PACKING_CHARGE_FREE_DELIVERY, MIN_CART_VALUE } from '../utils/cartConfig';
import { validateCartProducts } from '../services/productService';
import { AuthContext } from '../context/AuthContext';

import EmptyCart from '../components/cart/EmptyCart';
import CartSectionCard from '../components/cart/CartSectionCard';
import CartSummaryCard from '../components/cart/CartSummaryCard';
import '../styles/cart.css';

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // 1. Selectors
  const cartItems = useSelector((state) => state.cart.cartItems);
  const sectionNotes = useSelector((state) => state.cart.sectionNotes || {});
  const appliedCoupon = useSelector((state) => state.cart.appliedCoupon);

  // 2. Fetch HOME_SECTIONS ViewConfig for theme colors/icons
  const { data: configData } = useViewConfig("HOME_SECTIONS");
  const sectionsConfig = configData?.data?.sections || [];

  // State to hold product-level MOQs fetched in background
  const [productMOQs, setProductMOQs] = useState({});

  // Concurrency locking: track product IDs with in-flight quantity updates
  const [inFlightUpdates, setInFlightUpdates] = useState({});

  // 1. Logged-in user cart fetch and validation
  useEffect(() => {
    let isMounted = true;
    const loadUserCart = async () => {
      try {
        const res = await apiFetch('/api/cart');
        if (!res.ok) throw new Error('Failed to fetch cart');
        const data = await res.json();
        if (isMounted) {
          dispatch(setCartItems(data));
        }
      } catch (err) {
        console.error("Failed to load authenticated cart:", err);
      }
    };

    if (user) {
      loadUserCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dispatch]);

  // 2. Guest user cart batch validation
  useEffect(() => {
    let isMounted = true;
    const validateGuestCart = async () => {
      try {
        const itemsToValidate = cartItems.map(item => ({
          productId: item.productId,
          qty: item.qty
        }));
        if (itemsToValidate.length === 0) return;

        const data = await validateCartProducts(itemsToValidate);
        if (isMounted) {
          const isDifferent = 
            data.removedProducts.length > 0 || 
            data.quantityAdjustedProducts.length > 0 ||
            data.validProducts.length !== cartItems.length;

          if (isDifferent) {
            dispatch(setCartItems(data.validProducts));
          }
        }
      } catch (err) {
        console.error("Failed to validate guest cart:", err);
      }
    };

    if (!user) {
      validateGuestCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dispatch]);

  // 3. Build MOQ map from cart items
  useEffect(() => {
    const moqMap = {};
    cartItems.forEach(item => {
      moqMap[item.productId] = item.moq || 1;
    });
    setProductMOQs(moqMap);
  }, [cartItems]);

  // Helper to match API configs with grouped section cards
  const getSectionConfig = (sectionName) => {
    return sectionsConfig.find(
      (sec) => sec.title.toLowerCase() === sectionName.toLowerCase()
    );
  };

  // 3. Handlers
  const handleUpdateQty = useCallback(async (item, qty) => {
    // Prevent duplicate concurrent requests for the same product to avoid DB write race conditions
    if (inFlightUpdates[item.productId]) return;

    const minMoq = productMOQs[item.productId] || 1;
    const isSet = item.moqType === 'set';
    const minPcs = isSet ? minMoq * (item.unitsPerSet || 1) : minMoq;
    if (qty < minPcs) return;

    const originalQty = item.qty;

    if (user) {
      // Lock the controls for this specific item row
      setInFlightUpdates((prev) => ({ ...prev, [item.productId]: true }));

      // Optimistically update the local Redux state immediately
      dispatch(updateCartItemQty({ productId: item.productId, qty }));

      try {
        const res = await apiFetch('/api/cart', {
          method: 'PUT',
          body: JSON.stringify({ productId: item.productId, qty })
        });
        const data = await res.json();
        
        if (!res.ok) {
          // Rollback on error
          dispatch(updateCartItemQty({ productId: item.productId, qty: originalQty }));
          alert(data.message || 'Failed to update quantity');
        }
      } catch (err) {
        console.error(err);
        // Rollback on network failure
        dispatch(updateCartItemQty({ productId: item.productId, qty: originalQty }));
        alert('Failed to update quantity');
      } finally {
        // Unlock controls
        setInFlightUpdates((prev) => {
          const updated = { ...prev };
          delete updated[item.productId];
          return updated;
        });
      }
    } else {
      // Guest cart updates
      dispatch(addToCart({ ...item, qty }));
    }
  }, [user, dispatch, productMOQs, inFlightUpdates]);

  const handleRemove = useCallback(async (id) => {
    if (user) {
      try {
        const res = await apiFetch(`/api/cart/${id}`, {
          method: 'DELETE'
        });
        const data = await res.json();
        if (!res.ok) {
          alert(data.message || 'Failed to remove item');
          return;
        }
        dispatch(setCartItems(data));
      } catch (err) {
        console.error(err);
        alert('Failed to remove item');
      }
    } else {
      dispatch(removeFromCart(id));
    }
  }, [user, dispatch]);

  const handleUpdateNote = useCallback((section, note) => {
    dispatch(updateSectionNote({ section, note }));
  }, [dispatch]);

  const handleClearAll = async () => {
    if (window.confirm("Are you sure you want to clear your cart?")) {
      if (user) {
        try {
          const res = await apiFetch('/api/cart', {
            method: 'DELETE'
          });
          if (!res.ok) {
            const data = await res.json();
            alert(data.message || 'Failed to clear cart');
            return;
          }
          dispatch(clearCart());
        } catch (err) {
          console.error(err);
          alert('Failed to clear cart');
        }
      } else {
        dispatch(clearCart());
      }
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  // 4. Calculations & Groupings
  const groupedItems = useMemo(() => {
    const groups = {};
    cartItems.forEach((item) => {
      const section = item.section || "Unknown Section";
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(item);
    });
    return groups;
  }, [cartItems]);

  const orderedSections = useMemo(() => {
    const order = [
      "National Articles",
      "Premium & Imported",
      "Accessories",
      "Fragrance & Grooming"
    ];
    if (groupedItems["Unknown Section"]) {
      order.push("Unknown Section");
    }
    return order.filter((section) => groupedItems[section] && groupedItems[section].length > 0);
  }, [groupedItems]);

  // Overall statistics
  const totalQty = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + item.qty, 0);
  }, [cartItems]);

  const subtotal = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
  }, [cartItems]);

  const discountAmount = useMemo(() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.discountType === 'FixedAmount') {
      return Math.min(subtotal, Number(appliedCoupon.discountValue) || 0);
    }
    return Math.round((subtotal * (Number(appliedCoupon.discountValue) || 0)) / 100);
  }, [subtotal, appliedCoupon]);

  const deliveryCharge = useMemo(() => {
    if (cartItems.length === 0) return 0;
    return subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_CHARGE;
  }, [cartItems.length, subtotal]);

  const packingCharge = useMemo(() => {
    if (cartItems.length === 0) return 0;
    return subtotal >= FREE_DELIVERY_THRESHOLD ? PACKING_CHARGE_FREE_DELIVERY : PACKING_CHARGE_STANDARD;
  }, [cartItems.length, subtotal]);

  const grandTotal = useMemo(() => {
    return subtotal - discountAmount + deliveryCharge + packingCharge;
  }, [subtotal, discountAmount, deliveryCharge, packingCharge]);

  const handleApplyCoupon = useCallback((couponData) => {
    dispatch(setAppliedCoupon(couponData));
  }, [dispatch]);

  const handleRemoveCoupon = useCallback(() => {
    dispatch(removeAppliedCoupon());
  }, [dispatch]);


  // Check validation for every section
  const sectionsEligibility = useMemo(() => {
    const list = [];
    const coreSections = [
      "National Articles",
      "Premium & Imported",
      "Accessories",
      "Fragrance & Grooming"
    ];

    coreSections.forEach((sectionName) => {
      const items = groupedItems[sectionName] || [];
      const rule = SECTION_RULES[sectionName];

      if (items.length === 0) {
        list.push({
          name: sectionName,
          eligible: true,
          message: "Ready (No items)"
        });
        return;
      }

      if (rule.type === 'none') {
        list.push({
          name: sectionName,
          eligible: true,
          message: "Ready"
        });
      } else if (rule.type === 'moq') {
        const totalQty = items.reduce((acc, item) => acc + item.qty, 0);
        const limit = rule.value;
        const eligible = totalQty >= limit;
        list.push({
          name: sectionName,
          eligible,
          message: eligible ? "MOQ Met" : `Needs ${limit - totalQty} More Pcs`
        });
      } else if (rule.type === 'value') {
        const totalValue = items.reduce((acc, item) => acc + (item.price * item.qty), 0);
        const limit = rule.value;
        const eligible = totalValue >= limit;
        list.push({
          name: sectionName,
          eligible,
          message: eligible ? "Eligible" : `Needs ₹${limit - totalValue} More`
        });
      }
    });

    if (groupedItems["Unknown Section"]) {
      list.push({
        name: "Unknown Section",
        eligible: false,
        message: "Legacy items must be removed"
      });
    }

    return list;
  }, [groupedItems]);

  const isOverallEligible = useMemo(() => {
    if (cartItems.length === 0) return false;
    return sectionsEligibility.every((sec) => sec.eligible) && subtotal >= MIN_CART_VALUE;
  }, [cartItems, sectionsEligibility, subtotal]);

  // Mobile warning calculation
  const remainingForFree = FREE_DELIVERY_THRESHOLD - subtotal;
  const isFreeDelivery = subtotal >= FREE_DELIVERY_THRESHOLD;

  if (cartItems.length === 0) {
    return <EmptyCart />;
  }

  return (
    <div className="cart-page-wrapper">
      {/* Back button and page title */}
      <div className="cart-page-header">
        <div className="header-meta">
          <Link to="/shop" className="btn-back-link">
            <ArrowLeft size={16} />
            <span>Back to Shop</span>
          </Link>
          <h2>Your Cart <span className="section-count">({orderedSections.length} {orderedSections.length === 1 ? 'Section' : 'Sections'})</span></h2>
          <p className="header-subtitle">Review your cart and check the requirements for each section.</p>
        </div>
        <button 
          onClick={handleClearAll}
          className="btn-clear-cart"
          title="Empty entire shopping cart"
        >
          <Trash2 size={16} />
          <span>Clear Cart</span>
        </button>
      </div>

      <div className="cart-page-layout">
        {/* Left Column: Grouped Sections */}
        <div className="cart-sections-container">
          {orderedSections.map((sectionName, index) => (
            <CartSectionCard
              key={sectionName}
              sectionName={sectionName}
              sectionIndex={index + 1}
              items={groupedItems[sectionName]}
              note={sectionNotes[sectionName]}
              onUpdateNote={(note) => handleUpdateNote(sectionName, note)}
              onUpdateQty={handleUpdateQty}
              onRemove={handleRemove}
              sectionConfig={getSectionConfig(sectionName)}
              productMOQs={productMOQs}
              inFlightUpdates={inFlightUpdates}
            />
          ))}
        </div>

        {/* Right Column: Sticky Summary Panel (Desktop Only) */}
        <div className="cart-summary-sidebar-container">
          <CartSummaryCard
            totalQty={totalQty}
            subtotal={subtotal}
            deliveryCharge={deliveryCharge}
            packingCharge={packingCharge}
            discountAmount={discountAmount}
            grandTotal={grandTotal}
            sectionsEligibility={sectionsEligibility}
            isOverallEligible={isOverallEligible}
            appliedCoupon={appliedCoupon}
            onApplyCoupon={handleApplyCoupon}
            onRemoveCoupon={handleRemoveCoupon}
            onCheckout={handleCheckout}
          />
        </div>
      </div>

      {/* Floating Bottom Navigation Bar (Mobile Only) */}
      <div className="mobile-checkout-bar">
        <div className="mobile-bar-info">
          <div className="mobile-total-row">
            <span className="mobile-total-label">Total ({totalQty} Pcs)</span>
            <span className="mobile-total-price">₹{grandTotal.toLocaleString('en-IN')}</span>
          </div>
          {!isFreeDelivery && (
            <span className="mobile-shipping-warning">
              ⚠ Add ₹{remainingForFree.toLocaleString('en-IN')} more to get FREE delivery
            </span>
          )}
          {isFreeDelivery && (
            <span className="mobile-shipping-success">
              ✓ You have unlocked FREE delivery
            </span>
          )}
        </div>
        
        <button
          type="button"
          onClick={handleCheckout}
          disabled={!isOverallEligible}
          className="btn-mobile-checkout"
        >
          Checkout &rarr;
        </button>
      </div>
    </div>
  );
};

export default Cart;
